import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  GripHorizontal,
  Keyboard,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  buildCrosswordMask,
  crosswordDirectionDelta,
  normalizeCrosswordChar,
  normalizeCrosswordDirection,
  type CrosswordDirection,
} from '../../../utils/crosswordGrid';
import { FarsiKeyboard } from '../../shared/FarsiKeyboard';
import { useResponsive } from '../../shared/MobileResponsive';
import { FLOATING_KEYBOARD_ANIM_MS } from '../../../utils/learnerSlideModalMotion';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function floatingKeyboardBottomReservePx(): number {
  const rem =
    typeof document !== 'undefined'
      ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      : 16;
  let safe = 0;
  if (
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('padding-bottom: env(safe-area-inset-bottom)')
  ) {
    const probe = document.createElement('div');
    probe.style.paddingBottom = 'env(safe-area-inset-bottom)';
    probe.style.position = 'fixed';
    probe.style.visibility = 'hidden';
    document.body.appendChild(probe);
    safe = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    document.body.removeChild(probe);
  }
  return Math.max(0.75 * rem, safe);
}

function measureCenteredBottomKeyboardPos(kb: HTMLElement): { x: number; y: number } {
  const width = kb.offsetWidth;
  const height = kb.offsetHeight;
  const reserve = floatingKeyboardBottomReservePx();
  const x = Math.min(
    Math.max(8, (window.innerWidth - width) / 2),
    Math.max(8, window.innerWidth - width - 8)
  );
  const y = Math.max(8, window.innerHeight - height - reserve);
  return { x, y };
}

function directionArrowSymbol(direction: CrosswordDirection): string {
  switch (direction) {
    case 'rtl':
      return '←';
    case 'ttb':
      return '↓';
    case 'btt':
      return '↑';
    case 'ltr':
    default:
      return '→';
  }
}

export interface CrosswordPlayerEntry {
  id: number;
  entryWord: string;
  clue: string;
  startRow: number;
  startCol: number;
  direction: CrosswordDirection;
}

export interface CrosswordPlayerProps {
  rows: number;
  cols: number;
  entries: CrosswordPlayerEntry[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: string;
}

function ensureValueLength(value: string, mask: string): string {
  const chars = value.split('');
  const out: string[] = [];
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] === '#') {
      out.push('#');
      continue;
    }
    const ch = chars[i];
    if (ch == null || ch === '#' || ch === '') {
      out.push('.');
    } else {
      out.push(ch);
    }
  }
  return out.join('');
}

function isHorizontal(dir: CrosswordDirection): boolean {
  return dir === 'ltr' || dir === 'rtl';
}

function DirectionArrowIcon({ direction }: { direction: CrosswordDirection }) {
  const cls = 'w-3 h-3';
  switch (direction) {
    case 'rtl':
      return <ArrowLeft className={cls} />;
    case 'ttb':
      return <ArrowDown className={cls} />;
    case 'btt':
      return <ArrowUp className={cls} />;
    case 'ltr':
    default:
      return <ArrowRight className={cls} />;
  }
}

export function CrosswordPlayer({
  rows,
  cols,
  entries,
  value,
  onChange,
  disabled,
  hint,
}: CrosswordPlayerProps) {
  const { t, i18n } = useTranslation('common');
  const { t: tLearning } = useTranslation('learning');
  const { isMobile } = useResponsive();
  const uiDir = (i18n.language || 'en').toLowerCase().startsWith('fa') ? 'rtl' : 'ltr';

  const directionLabel = useCallback(
    (direction: CrosswordDirection) => {
      switch (direction) {
        case 'rtl':
          return tLearning('interactiveQuestions.crosswordDirRtl', { defaultValue: 'Right to left' });
        case 'ttb':
          return tLearning('interactiveQuestions.crosswordDirTtb', { defaultValue: 'Top to bottom' });
        case 'btt':
          return tLearning('interactiveQuestions.crosswordDirBtt', { defaultValue: 'Bottom to top' });
        case 'ltr':
        default:
          return tLearning('interactiveQuestions.crosswordDirLtr', { defaultValue: 'Left to right' });
      }
    },
    [tLearning]
  );

  const normalizedEntries = useMemo(
    () =>
      entries.map((e) => ({
        ...e,
        direction: normalizeCrosswordDirection(e.direction),
      })),
    [entries]
  );

  const mask = useMemo(
    () =>
      buildCrosswordMask(
        rows,
        cols,
        normalizedEntries.map((e) => ({
          word: e.entryWord,
          startRow: e.startRow,
          startCol: e.startCol,
          direction: e.direction,
        }))
      ),
    [rows, cols, normalizedEntries]
  );

  const gridValue = ensureValueLength(value || '', mask);
  const [focusIndex, setFocusIndex] = useState<number | null>(() => {
    const first = mask.split('').findIndex((ch) => ch !== '#');
    return first >= 0 ? first : null;
  });
  const [activeEntryId, setActiveEntryId] = useState<number | null>(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardMounted, setKeyboardMounted] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardPos, setKeyboardPos] = useState<{ x: number; y: number } | null>(null);
  // Mobile only: suppress OS soft keyboard while floating Farsi keyboard is open.
  const suppressOsKeyboard = showKeyboard && isMobile;
  const inputRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const keyboardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const focusIndexRef = useRef(focusIndex);
  focusIndexRef.current = focusIndex;

  const activeEntry = useMemo(
    () => normalizedEntries.find((e) => e.id === activeEntryId) ?? null,
    [normalizedEntries, activeEntryId]
  );

  const horizontal = normalizedEntries.filter((e) => isHorizontal(e.direction));
  const vertical = normalizedEntries.filter((e) => !isHorizontal(e.direction));

  const setCell = useCallback(
    (index: number, letter: string) => {
      if (disabled || mask[index] === '#') return;
      const chars = gridValue.split('');
      const normalized = letter ? normalizeCrosswordChar(letter) : '';
      chars[index] = normalized ? normalized.slice(0, 1) : '.';
      onChange(chars.join(''));
    },
    [disabled, gridValue, mask, onChange]
  );

  const focusCell = useCallback(
    (index: number | null) => {
      if (index == null || mask[index] === '#') return;
      setFocusIndex(index);
      requestAnimationFrame(() =>
        inputRefs.current.get(index)?.focus({ preventScroll: true })
      );
    },
    [mask]
  );

  const stepAlongDirection = useCallback(
    (from: number, direction: CrosswordDirection, reverse: boolean): number | null => {
      const { dr, dc } = crosswordDirectionDelta(direction);
      const sign = reverse ? -1 : 1;
      let r = Math.floor(from / cols) + sign * dr;
      let c = (from % cols) + sign * dc;
      while (r >= 0 && r < rows && c >= 0 && c < cols) {
        const idx = r * cols + c;
        if (mask[idx] !== '#') return idx;
        r += sign * dr;
        c += sign * dc;
      }
      return null;
    },
    [cols, rows, mask]
  );

  const nextPlayableRowMajor = useCallback(
    (from: number, dir: 1 | -1): number | null => {
      let i = from + dir;
      while (i >= 0 && i < mask.length) {
        if (mask[i] !== '#') return i;
        i += dir;
      }
      return null;
    },
    [mask]
  );

  const advanceFrom = useCallback(
    (from: number, reverse: boolean) => {
      if (activeEntry) {
        return stepAlongDirection(from, activeEntry.direction, reverse);
      }
      return nextPlayableRowMajor(from, reverse ? -1 : 1);
    },
    [activeEntry, nextPlayableRowMajor, stepAlongDirection]
  );

  const focusEntry = (entry: CrosswordPlayerEntry) => {
    setActiveEntryId(entry.id);
    const idx = entry.startRow * cols + entry.startCol;
    focusCell(idx);
  };

  const handleInsertLetter = useCallback(
    (char: string) => {
      const index = focusIndexRef.current;
      if (index == null || disabled) return;
      const letter = char.replace(/\s/g, '');
      if (!letter) return;
      setCell(index, letter.slice(-1));
      const next = advanceFrom(index, false);
      if (next != null) focusCell(next);
    },
    [advanceFrom, disabled, focusCell, setCell]
  );

  const handleBackspace = useCallback(() => {
    const index = focusIndexRef.current;
    if (index == null || disabled) return;
    const display =
      gridValue[index] === '.' || gridValue[index] === ' ' ? '' : gridValue[index] ?? '';
    if (display) {
      setCell(index, '');
      return;
    }
    const prev = advanceFrom(index, true);
    if (prev != null) {
      setCell(prev, '');
      focusCell(prev);
    }
  }, [advanceFrom, disabled, focusCell, gridValue, setCell]);

  const finishKeyboardUnmount = useCallback(() => {
    setKeyboardMounted(false);
    setKeyboardPos(null);
  }, []);

  const openKeyboard = useCallback(() => {
    setShowKeyboard(true);
    setKeyboardMounted(true);
    setKeyboardVisible(false);
  }, []);

  const closeKeyboard = useCallback(() => {
    setShowKeyboard(false);
    setKeyboardVisible(false);
    if (prefersReducedMotion()) {
      finishKeyboardUnmount();
    }
  }, [finishKeyboardUnmount]);

  // Dismiss OS soft keyboard when the floating Farsi keyboard opens (cells stay focusable via readOnly).
  useEffect(() => {
    if (!suppressOsKeyboard) return;
    const active = document.activeElement;
    if (!(active instanceof HTMLInputElement)) return;
    if (![...inputRefs.current.values()].includes(active)) return;
    active.blur();
    const index = focusIndexRef.current;
    if (index == null) return;
    const id = requestAnimationFrame(() => {
      inputRefs.current.get(index)?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [suppressOsKeyboard]);

  const handleKeyboardTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (!keyboardVisible && !showKeyboard) {
        finishKeyboardUnmount();
      }
    },
    [keyboardVisible, showKeyboard, finishKeyboardUnmount]
  );

  // Measure absolute left/top once before show — never use is-centered while interactive.
  useEffect(() => {
    if (!keyboardMounted) return;
    let cancelled = false;
    let attempts = 0;
    const tryUntilReady = () => {
      if (cancelled || attempts++ > 45) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return;
          if (!keyboardRef.current || keyboardRef.current.offsetWidth === 0) {
            tryUntilReady();
            return;
          }
          setKeyboardPos((prev) => {
            if (prev != null) return prev;
            const kb = keyboardRef.current;
            if (!kb || kb.offsetWidth === 0) return prev;
            return measureCenteredBottomKeyboardPos(kb);
          });
        });
      });
    };
    tryUntilReady();
    return () => {
      cancelled = true;
    };
  }, [keyboardMounted]);

  useEffect(() => {
    if (!keyboardMounted || !showKeyboard || keyboardVisible) return;
    if (keyboardPos == null) return;
    if (prefersReducedMotion()) {
      setKeyboardVisible(true);
      return;
    }
    const id = requestAnimationFrame(() => setKeyboardVisible(true));
    return () => cancelAnimationFrame(id);
  }, [keyboardMounted, showKeyboard, keyboardVisible, keyboardPos]);

  useEffect(() => {
    if (!keyboardMounted || keyboardVisible || showKeyboard) return;
    const timer = window.setTimeout(finishKeyboardUnmount, FLOATING_KEYBOARD_ANIM_MS + 50);
    return () => window.clearTimeout(timer);
  }, [keyboardMounted, keyboardVisible, showKeyboard, finishKeyboardUnmount]);

  useEffect(() => {
    if (!showKeyboard) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeKeyboard();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showKeyboard, closeKeyboard]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      // Prefer already-measured absolute coords; only seed from rect if somehow still null.
      let startLeft = keyboardPos?.x ?? 0;
      let startTop = keyboardPos?.y ?? 0;
      if (keyboardPos == null && keyboardRef.current) {
        const rect = keyboardRef.current.getBoundingClientRect();
        startLeft = rect.left;
        startTop = rect.top;
        setKeyboardPos({ x: startLeft, y: startTop });
      }
      dragRef.current = {
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        startLeft,
        startTop,
      };
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    [keyboardPos]
  );

  useEffect(() => {
    if (!keyboardMounted) return;
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current.isDragging) return;
      setKeyboardPos({
        x: dragRef.current.startLeft + (e.clientX - dragRef.current.startX),
        y: dragRef.current.startTop + (e.clientY - dragRef.current.startY),
      });
    };
    const onUp = () => {
      dragRef.current.isDragging = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [keyboardMounted]);

  const startCellIndex =
    activeEntry != null ? activeEntry.startRow * cols + activeEntry.startCol : null;

  const renderClueList = (list: CrosswordPlayerEntry[], title: string) => (
    <div>
      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">{title}</h4>
      <ol className="space-y-1 list-decimal list-inside">
        {list.map((e) => (
          <li key={`${title}-${e.id}`}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => focusEntry(e)}
              dir={uiDir}
              className={`hover:text-blue-600 dark:hover:text-blue-400 ${
                uiDir === 'rtl' ? 'text-right' : 'text-left'
              } ${
                activeEntryId === e.id
                  ? 'text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <span dir="auto">{e.clue || e.entryWord}</span>
              <span className="text-[0.7em] font-normal opacity-80 whitespace-nowrap">
                {` (${directionArrowSymbol(e.direction)} ${directionLabel(e.direction)})`}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <div className="space-y-3 w-full">
      {hint ? (
        <p className="text-xs text-gray-600 dark:text-gray-400" dir="auto">
          {hint}
        </p>
      ) : null}

      <div className="flex items-center justify-end">
        <button
          type="button"
          disabled={disabled}
          onClick={() => (showKeyboard ? closeKeyboard() : openKeyboard())}
          onMouseDown={(e) => e.preventDefault()}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm border transition-colors ${
            showKeyboard
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-400'
              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          title={showKeyboard ? 'Hide Farsi keyboard' : 'Show Farsi keyboard'}
          aria-label={showKeyboard ? 'Hide Farsi keyboard' : 'Show Farsi keyboard'}
        >
          <Keyboard className="w-4 h-4" />
          <span>Keyboard</span>
        </button>
      </div>

      <div className="w-full flex justify-center" dir="ltr">
        <div
          className="grid gap-0.5 p-2 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 w-full md:w-4/5"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({ length: rows * cols }, (_, index) => {
            const isBlock = mask[index] === '#';
            const display =
              gridValue[index] === '.' || gridValue[index] === ' ' ? '' : gridValue[index] ?? '';
            const showArrow = startCellIndex === index && activeEntry != null;

            if (isBlock) {
              return (
                <div
                  key={index}
                  className="aspect-square rounded bg-slate-300 dark:bg-gray-950"
                  aria-hidden
                />
              );
            }

            return (
              <div key={index} className="relative aspect-square">
                {showArrow ? (
                  <span
                    className="pointer-events-none absolute top-0.5 left-0.5 z-10 flex h-4 w-4 items-center justify-center rounded bg-blue-600 text-white shadow-sm"
                    aria-hidden
                  >
                    <DirectionArrowIcon direction={activeEntry.direction} />
                  </span>
                ) : null}
                <input
                  ref={(el) => {
                    if (el) inputRefs.current.set(index, el);
                    else inputRefs.current.delete(index);
                  }}
                  type="text"
                  inputMode={suppressOsKeyboard ? 'none' : 'text'}
                  readOnly={suppressOsKeyboard}
                  disabled={disabled}
                  value={display}
                  maxLength={1}
                  dir="auto"
                  aria-label={`Crossword cell row ${Math.floor(index / cols) + 1}, column ${(index % cols) + 1}`}
                  onFocus={() => setFocusIndex(index)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (!raw) {
                      setCell(index, '');
                      return;
                    }
                    const letter = raw.slice(-1);
                    setCell(index, letter);
                    const next = advanceFrom(index, false);
                    if (next != null) focusCell(next);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !display) {
                      const prev = advanceFrom(index, true);
                      if (prev != null) {
                        setCell(prev, '');
                        focusCell(prev);
                      }
                      e.preventDefault();
                    } else if (e.key === 'ArrowRight') {
                      const next = nextPlayableRowMajor(index, 1);
                      if (next != null) focusCell(next);
                      e.preventDefault();
                    } else if (e.key === 'ArrowLeft') {
                      const prev = nextPlayableRowMajor(index, -1);
                      if (prev != null) focusCell(prev);
                      e.preventDefault();
                    } else if (e.key === 'ArrowDown') {
                      const r = Math.floor(index / cols) + 1;
                      const c = index % cols;
                      if (r < rows) {
                        const down = r * cols + c;
                        if (mask[down] !== '#') focusCell(down);
                        else {
                          const next = stepAlongDirection(index, 'ttb', false);
                          if (next != null) focusCell(next);
                        }
                      }
                      e.preventDefault();
                    } else if (e.key === 'ArrowUp') {
                      const r = Math.floor(index / cols) - 1;
                      const c = index % cols;
                      if (r >= 0) {
                        const up = r * cols + c;
                        if (mask[up] !== '#') focusCell(up);
                        else {
                          const prev = stepAlongDirection(index, 'btt', false);
                          if (prev != null) focusCell(prev);
                        }
                      }
                      e.preventDefault();
                    }
                  }}
                  className={`absolute inset-0 w-full h-full text-center text-sm font-semibold rounded border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    focusIndex === index
                      ? 'border-blue-500 ring-2 ring-blue-400/60'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 text-sm">
        {renderClueList(
          horizontal,
          tLearning('interactiveQuestions.crosswordHorizontal', { defaultValue: 'Horizontal' })
        )}
        {renderClueList(
          vertical,
          tLearning('interactiveQuestions.crosswordVertical', { defaultValue: 'Vertical' })
        )}
      </div>

      {keyboardMounted && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={keyboardRef}
              className={`fixed z-[220] w-[min(95vw,28rem)] rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 overflow-hidden floating-farsi-keyboard-panel${keyboardVisible ? ' is-visible' : ''}`}
              onTransitionEnd={handleKeyboardTransitionEnd}
              style={
                keyboardPos != null
                  ? { left: keyboardPos.x, top: keyboardPos.y }
                  : {
                      left: 0,
                      top: 0,
                      visibility: 'hidden' as const,
                    }
              }
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={closeKeyboard}
                  onMouseDown={(e) => e.preventDefault()}
                  className="absolute top-1.5 right-1.5 z-10 flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-transform"
                  title={t('keyboard.close', { defaultValue: 'Close keyboard' })}
                  aria-label={t('keyboard.close', { defaultValue: 'Close keyboard' })}
                >
                  <X className="w-4 h-4" />
                </button>
                <div
                  role="button"
                  tabIndex={0}
                  onPointerDown={handlePointerDown}
                  className="flex items-center justify-center gap-1 py-1.5 px-9 bg-gray-200 dark:bg-gray-700 cursor-grab active:cursor-grabbing select-none border-b border-gray-300 dark:border-gray-600 touch-none"
                  title={t('keyboard.dragToMove', { defaultValue: 'Drag to move' })}
                  aria-label={t('keyboard.dragToMove', { defaultValue: 'Drag to move' })}
                >
                  <GripHorizontal className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('keyboard.dragToMoveShort', { defaultValue: 'Drag to move' })}
                  </span>
                </div>
                <div className="p-3 pb-[env(safe-area-inset-bottom)]">
                  <FarsiKeyboard
                    onInsert={handleInsertLetter}
                    onBackspace={handleBackspace}
                    onClose={closeKeyboard}
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
